import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cancelAiGeneration, getAiConfig, getAiGeneration, startAiGeneration, templateFileName } from './plantillaAiApi';
import './CrearAmbIa.css';

const STEPS = ['Pujar imatge', 'Generar plantilla', 'Obrir la pissarra'];
const PHASES = {
    preparing: 'Preparant la imatge…',
    connecting: 'Enviant el croquis a la IA…',
    analyzing: 'La IA està interpretant el croquis…',
    generating: 'Construint les posicions de la plantilla…',
    validating: 'Comprovant la plantilla abans d’obrir-la…',
};

function ProgressModal({ name, step, error, warnings = [], detail, startedAt, lastContactAt, reconnecting, onClose, onContinue }) {
    const dialog = useRef(null);
    const reviewing = warnings.length > 0;
    const active = !error && !reviewing;
    const [now, setNow] = useState(Date.now);
    const elapsed = Math.max(0, Math.floor((now - startedAt) / 1000));
    const duration = elapsed >= 60 ? `${Math.floor(elapsed / 60)} min ${String(elapsed % 60).padStart(2, '0')} s` : `${elapsed} s`;
    const stale = step === 1 && now - lastContactAt >= 10000;
    const positions = detail?.positionsReceived;
    useEffect(() => {
        if (!active) return;
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [active]);
    useEffect(() => {
        const element = dialog.current;
        const overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        element.showModal();
        return () => {
            element.close();
            document.body.style.overflow = overflow;
        };
    }, []);

    return createPortal(
        <dialog ref={dialog} className="crear-ia-modal" aria-labelledby="crear-ia-progress-title"
            onKeyDown={event => {
                if (event.key === 'Tab') {
                    const buttons = event.currentTarget.querySelectorAll('button');
                    const first = buttons[0];
                    const last = buttons[buttons.length - 1];
                    if (event.shiftKey && document.activeElement === first) {
                        event.preventDefault();
                        last.focus();
                    } else if (!event.shiftKey && document.activeElement === last) {
                        event.preventDefault();
                        first.focus();
                    }
                }
            }}
            onCancel={event => { event.preventDefault(); onClose(); }}>
            <h2 id="crear-ia-progress-title">{reviewing ? 'Revisa la plantilla' : 'Creant la plantilla'}</h2>
            <p className="crear-ia-filename">{name}</p>
            <ol aria-label="Progrés" className="crear-ia-progress">
                {STEPS.map((label, i) => <li key={label} className={i < step ? 'done' : i === step ? 'current' : ''} aria-current={i === step ? 'step' : undefined}>
                    <span className={`crear-ia-step ${i === step && !error && !reviewing ? 'working' : ''}`} aria-hidden="true">{i < step ? '✓' : i + 1}</span>
                    {label}
                </li>)}
            </ol>
            {reviewing ? <div className="crear-ia-warnings" role="alert">
                <p>La IA ha detectat punts que cal revisar:</p>
                <ul>{warnings.map((warning, i) => <li key={i}>{warning}</li>)}</ul>
            </div> : error ? <p className="crear-ia-error" role="alert">{error}</p> : <div className="crear-ia-feedback">
                <div role="status" aria-atomic="true">
                    <p className="crear-ia-activity">{step === 1 ? PHASES[detail?.phase] || 'Esperant el resultat de la IA…' : `${STEPS[step]}…`}</p>
                    {step === 1 && Number.isInteger(positions) && positions > 0 && <p className="crear-ia-count">
                        {positions === 1 ? '1 posició rebuda' : `${positions} posicions rebudes`}
                        <span>Pendents de la comprovació final</span>
                    </p>}
                    {(reconnecting || stale) && <p className="crear-ia-connection">
                        {reconnecting ? 'S’ha perdut el contacte amb el servidor. Tornant a connectar…' : 'Fa més de 10 segons que no rebem l’estat del servidor.'}
                    </p>}
                </div>
                <p className="crear-ia-elapsed" role="timer" aria-live="off">Temps transcorregut: {duration}</p>
                <p className="crear-ia-wait">{step === 1 && elapsed >= 60 ? 'Els croquis amb moltes posicions poden trigar uns minuts.' : 'Pots cancel·lar en qualsevol moment.'}</p>
            </div>}
            <div className="crear-ia-modal-actions">
                <button type="button" onClick={onClose}>{error || reviewing ? 'Tanca' : 'Cancel·la'}</button>
                {reviewing && <button type="button" className="crear-ia-continue" onClick={onContinue}>Obrir la pissarra</button>}
            </div>
        </dialog>, document.body
    );
}

function stopRun(run) {
    if (!run) return Promise.resolve();
    run.cancelled = true;
    run.controller.abort();
    clearTimeout(run.timer);
    run.wake?.();
    return run.id && !run.result ? cancelAiGeneration(run.id) : Promise.resolve();
}

function CrearAmbIa({ onOpen }) {
    const [config, setConfig] = useState(null);
    const [error, setError] = useState('');
    const [dragging, setDragging] = useState(false);
    const [progress, setProgress] = useState(null);
    const input = useRef(null);
    const mounted = useRef(true);
    const activeRun = useRef(null);
    const openEditor = useRef(onOpen);
    openEditor.current = onOpen;
    const disabled = !config?.enabled || progress !== null;

    useEffect(() => {
        mounted.current = true;
        const controller = new AbortController();
        getAiConfig(controller.signal).then(data => {
            if (!controller.signal.aborted) setConfig(data);
        }).catch(err => {
            if (!controller.signal.aborted) setError(err.message);
        });
        return () => {
            mounted.current = false;
            controller.abort();
            stopRun(activeRun.current).catch(() => {});
        };
    }, []);

    const openGenerated = () => {
        const run = activeRun.current;
        if (!mounted.current || !run?.result || run.cancelled) return;
        activeRun.current = null;
        setProgress(null);
        openEditor.current(run.result.svg, run.name);
    };

    const generate = async files => {
        if (disabled || activeRun.current) return;
        if (!files?.length) return;
        setError('');
        const file = files[0];
        if (files.length !== 1 || !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size === 0 || file.size > 10 * 1024 * 1024) {
            setError('Selecciona una sola imatge PNG, JPG o WebP de fins a 10 MB.');
            return;
        }
        const run = { controller: new AbortController(), cancelled: false, id: null, name: templateFileName(file.name) };
        activeRun.current = run;
        const current = () => mounted.current && !run.cancelled && activeRun.current === run;
        setProgress({ name: file.name, step: 0, startedAt: Date.now() });
        try {
            // Keep the upload response so a cancellation can also stop a late-created job.
            const started = await startAiGeneration(file);
            run.id = started.id;
            if (!current()) { await cancelAiGeneration(run.id); return; }
            const updateProgress = job => setProgress(previous => ({
                ...previous, step: 1, detail: job.progress, reconnecting: false, lastContactAt: Date.now(),
                startedAt: Number.isFinite(job.elapsedMs) && job.elapsedMs >= 0
                    ? Math.min(previous.startedAt, Date.now() - job.elapsedMs) : previous.startedAt,
            }));
            updateProgress(started);
            let failures = 0;
            while (current()) {
                let job;
                try {
                    job = await getAiGeneration(run.id, run.controller.signal);
                    failures = 0;
                } catch (err) {
                    if (!current()) return;
                    if (++failures >= 3) throw err;
                    setProgress(previous => ({ ...previous, reconnecting: true }));
                }
                if (!current()) return;
                if (job?.status === 'completed') {
                    run.result = job.result;
                    setProgress(previous => ({ ...previous, step: 2, warnings: job.result.warnings, reconnecting: false }));
                    if (job.result.warnings?.length) return;
                    await new Promise(resolve => requestAnimationFrame(resolve));
                    if (!current()) return;
                    openGenerated();
                    return;
                }
                if (job && job.status !== 'running') throw new Error(job.error || 'Generació cancel·lada.');
                if (job) updateProgress(job);
                await new Promise(resolve => { run.wake = resolve; run.timer = setTimeout(resolve, 2000); });
            }
        } catch (err) {
            if (!current()) return;
            stopRun(run).catch(() => {});
            activeRun.current = null;
            setProgress(previous => ({ ...previous, error: err.message }));
        }
    };

    const close = () => {
        const run = activeRun.current;
        activeRun.current = null;
        setProgress(null);
        stopRun(run).catch(() => {
            if (mounted.current) setError('No s’ha pogut confirmar la cancel·lació. La generació es pot completar en segon pla.');
        });
    };

    return <>
        <section className={`crear-ia-upload ${dragging ? 'dragging' : ''}`} aria-labelledby="crear-ia-title"
            onDragOver={event => { event.preventDefault(); if (!disabled) setDragging(true); }}
            onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false); }}
            onDrop={event => { event.preventDefault(); setDragging(false); generate(event.dataTransfer.files); }}>
            <div className="crear-ia-icon" aria-hidden="true">🖼️</div>
            <h3 id="crear-ia-title">Puja una imatge</h3>
            <p>Converteix un croquis amb IA</p>
            <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" aria-label="Imatge del croquis" hidden disabled={disabled}
                onChange={event => { generate(event.target.files); event.target.value = ''; }} />
            <button type="button" disabled={disabled} onClick={() => input.current.click()}>Selecciona imatge</button>
            <small>PNG, JPG o WebP · Fins a 10 MB</small>
            {config && !config.enabled && <p className="crear-ia-unavailable">La creació amb IA no està activada.</p>}
            {error && <p className="crear-ia-error" role="alert">{error}</p>}
        </section>
        {progress && <ProgressModal {...progress} onClose={close} onContinue={openGenerated} />}
    </>;
}

export default CrearAmbIa;
